import React, { createContext, useState, useContext } from 'react';
import api from '../axiosInstance';

const AnalysisContext = createContext(null);

export function AnalysisProvider({ children }) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [statusText, setStatusText] = useState('');
  const [analysisResults, setAnalysisResults] = useState(null);
  const [error, setError] = useState(null);
  const [hasCompleted, setHasCompleted] = useState(false);
  const [jobRole, setJobRole] = useState('');
  const [fileCount, setFileCount] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);

  const startAnalysis = async (formData, selectedJobRoleName, count) => {
    try {
      setIsAnalyzing(true);
      setHasCompleted(false);
      setError(null);
      setAnalysisResults(null);
      setProgressPercent(15);
      setStatusText(`Uploading ${count} resume${count > 1 ? 's' : ''}...`);
      setJobRole(selectedJobRoleName || 'Target Role');
      setFileCount(count || 1);
      setIsMinimized(false);

      // Progress simulation intervals while waiting for backend Gemini AI parsing
      const timer1 = setTimeout(() => {
        setProgressPercent(45);
        setStatusText('Extracting skills, experience & domain metrics with Gemini AI...');
      }, 1500);

      const timer2 = setTimeout(() => {
        setProgressPercent(75);
        setStatusText('Calculating similarity scores & ranking candidates...');
      }, 4000);

      const response = await api.post('/upload', formData);

      clearTimeout(timer1);
      clearTimeout(timer2);

      if (response.status === 200) {
        setProgressPercent(100);
        setStatusText('Analysis Complete!');
        setAnalysisResults(response.data);
        setIsAnalyzing(false);
        setHasCompleted(true);
      } else {
        throw new Error('Server returned an unexpected status code.');
      }
    } catch (err) {
      console.error('Global Analysis Error:', err);
      setIsAnalyzing(false);
      setHasCompleted(false);
      setProgressPercent(0);
      setError(
        err.response?.data?.message || err.message || 'Analysis failed. Please try again.'
      );
    }
  };

  const resetAnalysis = () => {
    setIsAnalyzing(false);
    setHasCompleted(false);
    setProgressPercent(0);
    setStatusText('');
    setAnalysisResults(null);
    setError(null);
  };

  const dismissCompletedNotification = () => {
    setHasCompleted(false);
  };

  const toggleMinimize = () => {
    setIsMinimized((prev) => !prev);
  };

  return (
    <AnalysisContext.Provider
      value={{
        isAnalyzing,
        progressPercent,
        statusText,
        analysisResults,
        error,
        hasCompleted,
        jobRole,
        fileCount,
        isMinimized,
        startAnalysis,
        resetAnalysis,
        dismissCompletedNotification,
        toggleMinimize,
      }}
    >
      {children}
    </AnalysisContext.Provider>
  );
}

export function useAnalysis() {
  const context = useContext(AnalysisContext);
  if (!context) {
    throw new Error('useAnalysis must be used within an AnalysisProvider');
  }
  return context;
}

export default AnalysisContext;
